import { NavLink, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Layers, ScanLine, FileText, GitCompare, Bookmark, LogOut, CreditCard, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { openBillingPortal } from "@/lib/api";
import { toast } from "sonner";
import OnboardingModal from "@/components/OnboardingModal";

const nav = [
  { to: "/dashboard", label: "Inicio", icon: Home, testid: "nav-dashboard" },
  { to: "/builder", label: "Crear oferta", icon: Layers, testid: "nav-builder" },
  { to: "/analyzer", label: "Analizar cliente", icon: ScanLine, testid: "nav-analyzer" },
  { to: "/proposal", label: "Propuesta", icon: FileText, testid: "nav-proposal" },
  { to: "/value-ladder", label: "Escalera", icon: GitCompare, testid: "nav-value-ladder" },
  { to: "/library", label: "Biblioteca", icon: Bookmark, testid: "nav-library" },
];

function TrialBanner({ user }) {
  if (!user || user.is_admin) return null;
  if (user.subscription_status !== "trialing" || !user.trial_ends_at) return null;
  const end = new Date(user.trial_ends_at);
  const daysLeft = Math.max(0, Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24)));
  return (
    <div className="bg-foreground text-white border-b border-black/[0.08]" data-testid="trial-banner">
      <div className="px-6 py-2 text-center text-[12px] font-medium flex items-center justify-center gap-2 flex-wrap">
        <span className="w-1.5 h-1.5 rounded-full bg-[#A8D5BA] animate-pulse" />
        <span>Prueba activa · {daysLeft} {daysLeft === 1 ? "día restante" : "días restantes"}</span>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const current = nav.find((n) => loc.pathname.startsWith(n.to))?.label || "Strato";

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const goPortal = async () => {
    try {
      const r = await openBillingPortal();
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo abrir el portal");
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* SIDEBAR Apple/Notion minimalista */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col sticky top-0 h-screen px-4 py-6 border-r border-black/[0.06] bg-background">
        <Link to="/" className="flex items-center gap-2 px-2 mb-10" data-testid="sidebar-logo">
          <div className="w-7 h-7 rounded-full bg-foreground grid place-items-center">
            <span className="deco-star text-white w-3.5 h-3.5" />
          </div>
          <div>
            <div className="font-display text-lg leading-none">
              <span className="font-black">strato</span>
              <span className="font-normal text-foreground/55 ml-1 text-sm">by abstudio</span>
            </div>
            <div className="text-[10px] text-foreground/55 mt-1">Tu copiloto creativo</div>
          </div>
        </Link>

        <nav className="flex-1 space-y-0.5">
          {nav.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-colors ${
                  isActive
                    ? "bg-foreground text-white"
                    : "text-foreground/70 hover:bg-black/[0.04]"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              <span>{label}</span>
            </NavLink>
          ))}
          {user?.is_admin ? (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-colors mt-3 border-t border-black/[0.06] pt-4 ${
                  isActive
                    ? "bg-foreground text-white"
                    : "text-foreground/70 hover:bg-black/[0.04]"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4" strokeWidth={2} />
              <span>Admin</span>
            </NavLink>
          ) : null}
        </nav>

        {/* User card limpio */}
        {user && (
          <div className="app-card p-3 mt-4" data-testid="sidebar-user-card">
            <div className="flex items-center gap-2.5 mb-2.5">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-black/10" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-foreground text-white grid place-items-center font-display font-black text-xs">
                  {(user.name || user.email)[0]?.toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-semibold truncate leading-tight">{user.name || user.email.split("@")[0]}</div>
                <div className="text-[10px] text-foreground/55 truncate">{user.email}</div>
              </div>
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={goPortal}
                className="flex-1 text-[10px] font-medium py-1.5 rounded-lg bg-transparent hover:bg-black/[0.04] text-foreground/70 flex items-center justify-center gap-1"
                data-testid="open-portal-btn"
                title="Plan"
              >
                <CreditCard className="w-3 h-3" /> Plan
              </button>
              <button
                onClick={doLogout}
                className="flex-1 text-[10px] font-medium py-1.5 rounded-lg bg-transparent hover:bg-black/[0.04] text-foreground/70 flex items-center justify-center gap-1"
                data-testid="logout-btn"
              >
                <LogOut className="w-3 h-3" /> Salir
              </button>
            </div>
          </div>
        )}
      </aside>

      {/* MAIN */}
      <main className="flex-1 min-w-0 relative flex flex-col">
        <TrialBanner user={user} />
        <header className="sticky top-0 z-30 bg-background/85 backdrop-blur-xl border-b border-black/[0.05]">
          <div className="h-12 px-8 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[12px]">
              <span className="text-foreground/55">strato</span>
              <span className="text-foreground/25">/</span>
              <span className="font-semibold" data-testid="current-section">{current}</span>
            </div>
            <Link to="/" className="text-[11px] font-medium text-foreground/55 hover:text-foreground transition-colors" data-testid="back-to-landing">
              ← Volver al inicio
            </Link>
          </div>
        </header>
        <div className="flex-1 px-8 md:px-12 py-10 max-w-[1280px] mx-auto relative w-full">
          <Outlet />
        </div>
        <footer className="mt-auto border-t border-black/[0.05] bg-foreground/[0.015]">
          <div className="max-w-[1280px] mx-auto px-8 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-foreground/55">
            <p>© {new Date().getFullYear()} ALBA BAENA RUIZ LTDA. · stratoapp.online</p>
            <nav className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <Link to="/legal" className="hover:text-foreground transition font-medium" data-testid="applayout-legal-link">Información legal</Link>
              <span className="text-foreground/20">·</span>
              <Link to="/privacidad" className="hover:text-foreground transition">Privacidad</Link>
              <Link to="/terminos" className="hover:text-foreground transition">Términos</Link>
              <Link to="/cookies" className="hover:text-foreground transition">Cookies</Link>
            </nav>
          </div>
        </footer>
      </main>
      <OnboardingModal />
    </div>
  );
}
